import React from 'react';
import { CalendarDays } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sparkline } from '../ui/sparkline';
import { Aide } from '../ui/aide';
import { WORK_TYPE_LABELS } from '../../lib/maintenanceLabels';
import { formatMontant } from '../../lib/formats';

export const MAINTENANCE_WINDOWS = { 7: '7 jours', 30: '30 jours', 90: '90 jours', 365: '12 mois' };
const colors = ['--chart-1', '--chart-2', '--chart-3', '--chart-4'];
const money = value => formatMontant(Number(value));

/** Même date métier que les KPI : clôture, sinon création ; aucune somme globale depuis une page. */
export function maintenanceSpendSeries(orders, days, now = new Date()) {
  const end = now.getTime(), start = end - Number(days) * 86400000;
  const byDay = new Map();
  for (const order of orders) {
    const date = new Date(order.completed_at || order.created_at);
    const amount = Number(order.total_cost || 0);
    if (!Number.isFinite(date.getTime()) || date.getTime() < start || date.getTime() > end || !Number.isFinite(amount)) continue;
    const key = date.toISOString().slice(0, 10);
    byDay.set(key, (byDay.get(key) || 0) + amount);
  }
  return [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, count]) => ({ date, count: Math.round(count) }));
}

export default function MaintenanceSpendHeader({ kpis, loading, error, periodDays, onPeriodChange, onOpenOrders, series, truncated = false, showTrend = true }) {
  const types = Object.entries(kpis?.cost_by_type || {}).filter(([, value]) => Number(value) > 0);
  const natureTotal = types.reduce((sum, [, value]) => sum + Number(value), 0);
  const hasTrend = series?.some(point => point.count !== 0);
  const known = kpis?.cost_total !== null && kpis?.cost_total !== undefined;
  const Total = onOpenOrders ? 'button' : 'div';
  return <section aria-label="Synthèse des dépenses de maintenance" aria-busy={loading} data-testid="maintenance-spend-header"
    className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-5 gap-y-2 border-t border-border/60 py-2.5 lg:grid-cols-[auto_minmax(0,1fr)_auto]">
    <Total type={onOpenOrders ? 'button' : undefined} onClick={onOpenOrders} disabled={onOpenOrders ? loading : undefined}
      title={onOpenOrders ? 'Consulter les ordres de travail' : undefined} className="min-h-11 min-w-0 rounded text-left sm:min-h-0">
      <p className="text-xs text-muted-foreground">Dépenses atelier</p>
      <p className="break-words font-mono text-lg font-semibold tabular-nums leading-6" aria-live="polite">
        {loading ? '…' : error || !known ? '—' : money(kpis.cost_total)}
      </p>
    </Total>
    <div className="lg:order-3">
      <Select value={String(periodDays)} onValueChange={value => onPeriodChange(Number(value))}>
        <SelectTrigger aria-label="Période de la synthèse maintenance" className="h-11 w-[140px] gap-2 text-xs sm:h-8">
          <CalendarDays className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" /><SelectValue />
        </SelectTrigger>
        <SelectContent>{Object.entries(MAINTENANCE_WINDOWS).map(([days, label]) => <SelectItem key={days} value={days}>{label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div className="col-span-2 min-w-0 lg:order-2 lg:col-span-1">
      {loading || error || !known ? <p className="text-xs text-muted-foreground" role="status">
        {loading ? 'Actualisation de la synthèse…' : error ? 'Dépenses indisponibles pour cette période.' : 'Dépenses non renseignées sur cette période.'}
      </p> : <div className={`grid min-w-0 items-center gap-x-5 gap-y-2 ${showTrend && hasTrend ? 'sm:grid-cols-[minmax(0,1fr)_minmax(140px,220px)]' : ''}`}>
        <div className="min-w-0">
          {types.length ? <>
            <div className="mb-1.5 flex h-1 overflow-hidden rounded-full bg-muted" aria-hidden="true">
              {types.map(([type, amount], index) => <span key={type} style={{ width: `${Number(amount) / natureTotal * 100}%`, backgroundColor: `hsl(var(${colors[index % colors.length]}))` }} />)}
            </div>
            <dl aria-label="Dépense par nature" className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {types.map(([type, amount], index) => <div key={type} className="flex min-w-0 flex-wrap items-baseline gap-1.5">
                <dt className="flex items-center gap-1.5 text-muted-foreground"><span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: `hsl(var(${colors[index % colors.length]}))` }} />{WORK_TYPE_LABELS[type] || 'Autre'}</dt>
                <dd className="font-mono tabular-nums">{money(amount)}</dd>
              </div>)}
            </dl>
          </> : <p className="text-xs text-muted-foreground">{Number(kpis.cost_total) === 0 ? 'Aucune dépense chiffrée sur la période.' : 'Répartition par nature indisponible.'}</p>}
        </div>
        {showTrend && hasTrend && <div className="min-w-0"><Sparkline key={`${periodDays}:${series.length}`} height={26} points={series} label="Dépense atelier par jour" valueLabel="Montant" formatValue={money} /></div>}
        {showTrend && truncated && <div className={hasTrend ? 'sm:col-span-2' : ''}><Aide titre="Évolution partielle" libelle="Évolution partielle">Seuls les ordres chargés sont représentés. Le total et les natures couvrent toute la période.</Aide></div>}
      </div>}
    </div>
  </section>;
}
